import { cn } from "@/lib/utils";
import { BatteryFull, Signal, Wifi } from "lucide-react";
import type { ReactNode } from "react";

interface PhoneFrameProps {
  children: ReactNode;
  // Optional screen heading (e.g. "Your Fydo Status").
  title?: string;
  // "brand" / "whatsapp" paint the status bar (primary red / WhatsApp green)
  // so it runs into a matching app header placed first in `children`.
  statusBar?: "light" | "brand" | "whatsapp";
  // Override the screen's content layout (defaults to a padded stack).
  contentClassName?: string;
}

const statusBarClass = {
  light: "text-ink",
  brand: "bg-primary text-white",
  whatsapp: "bg-tone-mint-ink text-white",
};

// Decorative iPhone-style device: dark frame with a metal edge highlight,
// side buttons, Dynamic Island, status bar and home indicator. The screen is
// locked to a real iPhone's 9 : 19.5 shape at one phone width (240px, 272px
// from sm) - letting it stretch wider made it read as a tablet.
// Everything except `children` is aria-hidden: it's a picture of a phone,
// and the screen content is what matters to assistive tech.
const PhoneFrame = ({
  children,
  title,
  statusBar = "light",
  contentClassName = "grid content-start gap-2.5 px-4 pt-1 pb-9 text-[13px]",
}: PhoneFrameProps) => (
  <div className="relative mx-auto w-full max-w-[240px] sm:max-w-[272px]">
    {/* Side buttons: action + volume on the left, power on the right. */}
    <span aria-hidden="true" className="absolute top-[18%] -left-[3px] h-7 w-[3px] rounded-l-sm bg-ink" />
    <span aria-hidden="true" className="absolute top-[26%] -left-[3px] h-11 w-[3px] rounded-l-sm bg-ink" />
    <span aria-hidden="true" className="absolute top-[35%] -left-[3px] h-11 w-[3px] rounded-l-sm bg-ink" />
    <span aria-hidden="true" className="absolute top-[28%] -right-[3px] h-[68px] w-[3px] rounded-r-sm bg-ink" />

    <div className="rounded-[46px] bg-ink p-[9px] shadow-phone inset-ring-[1.5px] inset-ring-white/15">
      <div className="relative flex aspect-[9/19.5] flex-col overflow-hidden rounded-[38px] bg-white">
        {/* Three columns like a real iPhone: time centred in the left
            "ear", the Dynamic Island in the middle, status icons centred in
            the right ear - so the island can never overlap either side. */}
        <div
          aria-hidden="true"
          className={cn(
            "grid h-12 shrink-0 grid-cols-[1fr_auto_1fr] items-center px-3 text-[13px] font-semibold",
            statusBarClass[statusBar],
          )}
        >
          {/* 9:41 - the time Apple shows on every product shot. */}
          <span className="justify-self-center">9:41</span>
          <span className="h-6 w-[76px] rounded-full bg-ink" />
          <span className="flex items-center gap-[3px] justify-self-center">
            <Signal className="size-3" strokeWidth={2.75} />
            <Wifi className="size-3" strokeWidth={2.75} />
            <BatteryFull className="size-[18px]" strokeWidth={2} />
          </span>
        </div>

        <div className={cn("min-h-0 flex-1", contentClassName)}>
          {title && <span className="text-[15px] font-extrabold">{title}</span>}
          {children}
        </div>

        <span
          aria-hidden="true"
          className="absolute bottom-2 left-1/2 h-[5px] w-[108px] -translate-x-1/2 rounded-full bg-ink/85"
        />
      </div>
    </div>
  </div>
);

export default PhoneFrame;
